import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { RenderIf } from 'react-rainbow-components';
import Badge from 'components/Badge';
import SettingIcon from 'components/icons/setting';
import ExportIcon from 'components/icons/export2';
import useNavigateWithQuery from 'hooks/useNavigateWithQuery';
import { Pipeline } from 'data/firestore/agent/pipeline/types';
import BoardIcon from 'components/icons/board';
import useUserRoles from 'hooks/useUserRoles';
import useManagePipelines from '../hooks/useManagePipelines';
import {
    ItemContainer,
    ItemTitleContainer,
    ItemTitle,
    ItemDescription,
    ItemActions,
    StyledButtonIcon,
    StyledRecordField,
    ItemStages,
} from './styled';

interface Params {
    pipeline: Pipeline;
}

const PipelineCard = ({
    pipeline,
}: Params) => {
    const { agentId = '' } = useParams();
    const { isAdmin } = useUserRoles();
    const navigate = useNavigateWithQuery();
    const { export: exportPipeline } = useManagePipelines({ agentId });

    const description = pipeline.description ?? 'No description provided';

    const isBuiltIn = !pipeline.removable;

    const handleEditPipeline = useCallback(
        () => navigate(`/${agentId}/pipelines/${pipeline.id}/edit`),
        [agentId, pipeline, navigate],
    );

    const handleExportPipeline = useCallback(
        () => exportPipeline(pipeline.id, pipeline.name),
        [exportPipeline, pipeline.id, pipeline.name],
    );

    return (
        <ItemContainer>
            <ItemTitleContainer>
                <ItemTitle isBuiltIn={isBuiltIn}>
                    {pipeline.name}
                    <RenderIf isTrue={isBuiltIn}>
                        <Badge size="small" label="Built-In" variant="brand" />
                    </RenderIf>
                </ItemTitle>
                <ItemActions>
                    <StyledButtonIcon
                        borderRadius="semi-square"
                        size="small"
                        icon={<ExportIcon />}
                        tooltip="Export pipeline"
                        onClick={handleExportPipeline}
                    />
                    <RenderIf isTrue={!isBuiltIn || isAdmin}>
                        <StyledButtonIcon
                            borderRadius="semi-square"
                            size="small"
                            icon={<SettingIcon />}
                            onClick={handleEditPipeline}
                        />
                    </RenderIf>
                </ItemActions>
            </ItemTitleContainer>
            <ItemDescription>{description}</ItemDescription>
            <ItemStages>
                <BoardIcon />
                <StyledRecordField
                    label="Stages:"
                    value={pipeline.stages.length}
                />
            </ItemStages>
        </ItemContainer>
    );
};

export default PipelineCard;
